import React, { useState } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  IndianRupee, Droplets, Gauge, FileText,
  AlertTriangle, TrendingUp, TrendingDown,
  ChevronRight, X, Edit2, Trash2, Eye,
  Bus, Wrench,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/* ─── Static data ────────────────────────────────────────────────── */

const KPI_DATA = [
  { icon: IndianRupee, bg: 'bg-violet-500', label: 'Total Fuel Cost',   value: '₹2,77,720.00', delta: -12.5 },
  { icon: Droplets,    bg: 'bg-blue-500',   label: 'Total Quantity',    value: '308.00 L',      delta: +8.3  },
  { icon: Gauge,       bg: 'bg-orange-500', label: 'Average Mileage',   value: '7.45 km/L',     delta: -3.2  },
  { icon: FileText,    bg: 'bg-slate-500',  label: 'Total Entries',     value: '5',             delta: null  },
];

const ALERTS = [
  {
    id: 1,
    message: 'Alert: Mileage dropped for BUS003 by 18% this month (5.92 km/L vs 7.21 km/L last month). Please check the vehicle.',
    detail: {
      vehicle:        'BUS003',
      driver:         'Rahul Sharma',
      drop:           '18%',
      thisMileage:    '5.92 km/L',
      lastMileage:    '7.21 km/L',
      lastService:    '10 Jan 2026',
      recommendation: 'Schedule an immediate vehicle inspection. Possible causes: tyre under-inflation, air filter blockage, engine issue, or fuel quality problem.',
      entries: [
        { date: '20 May 2026', qty: 55, cost: 4950, mileage: 5.92, odometer: '12,850 km' },
        { date: '04 May 2026', qty: 60, cost: 5400, mileage: 6.10, odometer: '12,200 km' },
        { date: '18 Apr 2026', qty: 58, cost: 5220, mileage: 7.21, odometer: '11,480 km' },
      ],
    },
  },
];

const TREND_DATA = [
  { date: '01 May', cost: 45000 },
  { date: '05 May', cost: 38000 },
  { date: '09 May', cost: 42000 },
  { date: '13 May', cost: 30000 },
  { date: '17 May', cost: 48000 },
  { date: '21 May', cost: 27720 },
];

const MILEAGE_DATA = [
  { vehicle: 'BUS001', thisMonth: 8.18, lastMonth: 6.10 },
  { vehicle: 'BUS002', thisMonth: 7.14, lastMonth: 7.45 },
  { vehicle: 'BUS003', thisMonth: 5.92, lastMonth: 7.21 },
  { vehicle: 'BUS004', thisMonth: 4.82, lastMonth: 5.02 },
];

const RECENT_ENTRIES = [
  { id: 1, date: '22 May 2026, 06:30 PM', vehicle: 'BUS001', driver: 'Rahul Sharma', fuelType: 'Diesel', qty: 58, cost: 5220, mileage: 7.76, odometer: '13,300 km', payment: 'Upi',  anomaly: false },
  { id: 2, date: '21 May 2026, 07:10 AM', vehicle: 'BUS002', driver: 'Amit Verma',   fuelType: 'Diesel', qty: 70, cost: 6300, mileage: 7.14, odometer: '9,700 km',  payment: 'Card', anomaly: false },
  { id: 3, date: '20 May 2026, 05:45 PM', vehicle: 'BUS003', driver: 'Rahul Sharma', fuelType: 'Diesel', qty: 55, cost: 4950, mileage: 5.92, odometer: '12,850 km', payment: 'Upi',  anomaly: true  },
  { id: 4, date: '18 May 2026, 06:20 PM', vehicle: 'BUS002', driver: 'Amit Verma',   fuelType: 'Diesel', qty: 65, cost: 5850, mileage: null, odometer: '9,200 km',  payment: 'Cash', anomaly: true  },
  { id: 5, date: '17 May 2026, 06:15 PM', vehicle: 'BUS001', driver: 'Rahul Sharma', fuelType: 'Diesel', qty: 60, cost: 5400, mileage: null, odometer: '12,400 km', payment: 'Cash', anomaly: false },
];

const fmtINR = (v) => `₹${new Intl.NumberFormat('en-IN').format(v)}`;

/* ─── KPI Card ───────────────────────────────────────────────────── */
const KpiCard = ({ icon: Icon, bg, label, value, delta }) => {
  // simple rule: up = green, down = red, null = hidden
  const isUp  = delta !== null && delta > 0;
  const color = delta === null ? '' : isUp ? 'text-green-600' : 'text-red-500';
  const DIcon = isUp ? TrendingUp : TrendingDown;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
      <div className={`${bg} p-3 rounded-xl shrink-0`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 font-medium mb-0.5">{label}</p>
        <p className="text-xl font-bold text-gray-900 truncate">{value}</p>
        {delta !== null && (
          <span className={`flex items-center gap-0.5 text-xs font-medium mt-1 ${color}`}>
            <DIcon className="h-3 w-3" />
            {isUp ? '+' : ''}{delta}% vs last month
          </span>
        )}
      </div>
    </div>
  );
};

/* ─── Mileage cell colour ────────────────────────────────────────── */
const mileageCls = (v) =>
  !v         ? 'text-gray-400' :
  v < 6      ? 'text-red-600 font-semibold' :
  v < 7      ? 'text-amber-600 font-semibold' :
  v >= 8     ? 'text-green-600 font-semibold' :
               'text-gray-700';

/* ─── Main ───────────────────────────────────────────────────────── */
const FuelOverview = ({ onViewEntries }) => {
  const [dismissed,    setDismissed]    = useState([]);
  const [activeDetail, setActiveDetail] = useState(null);
  const [viewRow,      setViewRow]      = useState(null);

  const visibleAlerts = ALERTS.filter((a) => !dismissed.includes(a.id));

  return (
    <div className="space-y-5">

      {/* ── Alert Strip ── */}
      {visibleAlerts.map((alert) => (
        <div
          key={alert.id}
          className="flex items-center justify-between gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3"
        >
          <div className="flex items-center gap-3 min-w-0">
            <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
            <p className="text-sm text-red-800 font-medium leading-snug">{alert.message}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setActiveDetail(alert.detail)}
              className="text-xs font-semibold text-red-600 border border-red-300 rounded-lg px-3 py-1.5 hover:bg-red-100 transition-colors whitespace-nowrap"
            >
              View Details
            </button>
            <button
              onClick={() => setDismissed((p) => [...p, alert.id])}
              className="text-red-400 hover:text-red-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {KPI_DATA.map((k) => <KpiCard key={k.label} {...k} />)}
      </div>

      {/* ── Charts ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        {/* Fuel Cost Trend */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-800">Fuel Cost Trend</h2>
            <span className="text-xs text-gray-400 border border-gray-200 rounded-md px-2 py-1">Cost (₹)</span>
          </div>
          <ResponsiveContainer width="100%" height={210}>
            <LineChart data={TREND_DATA} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
              <Tooltip
                formatter={(v) => [fmtINR(v), 'Cost']}
                contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #e5e7eb' }}
              />
              <Line type="monotone" dataKey="cost" stroke="#3b82f6" strokeWidth={2.5}
                dot={{ r: 4, fill: '#3b82f6', strokeWidth: 0 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Mileage by Vehicle */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-gray-800 mb-2">Mileage by Vehicle (km/L)</h2>
          <div className="flex items-center gap-4 mb-3">
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <span className="inline-block w-3 h-2.5 rounded-sm bg-blue-500" /> This Month
            </span>
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <span className="inline-block w-3 h-2.5 rounded-sm bg-gray-300" /> Last Month
            </span>
          </div>
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={MILEAGE_DATA} margin={{ top: 4, right: 4, left: -24, bottom: 0 }} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="vehicle" tick={{ fontSize: 11, fill: '#9ca3af' }} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} domain={[0, 12]} />
              <Tooltip
                formatter={(v, name) => [`${v} km/L`, name === 'thisMonth' ? 'This Month' : 'Last Month']}
                contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #e5e7eb' }}
              />
              <Bar dataKey="thisMonth" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="lastMonth" fill="#d1d5db" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Recent Fuel Entries ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-800">Recent Fuel Entries</h2>
          <button
            onClick={onViewEntries}
            className="flex items-center gap-1 text-xs text-blue-600 font-semibold hover:underline"
          >
            View All Entries <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                {['Date & Time','Vehicle','Driver','Fuel Type','Qty (L)','Cost (₹)','Mileage','Odometer','Payment','Actions'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {RECENT_ENTRIES.map((row) => (
                <tr key={row.id} className={`border-t border-gray-50 ${row.anomaly ? 'bg-amber-50' : 'hover:bg-gray-50'}`}>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap text-xs">{row.date}</td>
                  <td className="px-4 py-3 font-semibold text-gray-900">{row.vehicle}</td>
                  <td className="px-4 py-3 text-gray-700">{row.driver}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">{row.fuelType}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{row.qty}</td>
                  <td className="px-4 py-3 text-gray-700">{fmtINR(row.cost)}</td>
                  <td className={`px-4 py-3 text-sm ${mileageCls(row.mileage)}`}>
                    {row.mileage ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-xs whitespace-nowrap">{row.odometer}</td>
                  <td className="px-4 py-3 text-gray-700 capitalize">{row.payment}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setViewRow(row)}
                        className="text-gray-400 hover:text-blue-600 transition-colors" title="View"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={onViewEntries}
                        className="text-gray-400 hover:text-blue-600 transition-colors" title="Edit in Entries tab"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-gray-100">
          <p className="text-xs text-gray-400">Showing 1 to 5 of 5 entries</p>
        </div>
      </div>

      {/* ── Row View Modal ── */}
      <Dialog open={!!viewRow} onOpenChange={(o) => !o && setViewRow(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Entry Details</DialogTitle>
          </DialogHeader>
          {viewRow && (
            <div className="space-y-3 text-sm mt-1">
              {[
                ['Date & Time', viewRow.date],
                ['Vehicle',     viewRow.vehicle],
                ['Driver',      viewRow.driver],
                ['Fuel Type',   viewRow.fuelType],
                ['Quantity',    `${viewRow.qty} L`],
                ['Cost',        fmtINR(viewRow.cost)],
                ['Mileage',     viewRow.mileage ? `${viewRow.mileage} km/L` : '—'],
                ['Odometer',    viewRow.odometer],
                ['Payment',     viewRow.payment],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between border-b border-gray-50 pb-2">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-medium text-gray-800 capitalize">{val}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Alert Detail Modal ── */}
      <Dialog open={!!activeDetail} onOpenChange={(o) => !o && setActiveDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Mileage Drop Alert — {activeDetail?.vehicle}
            </DialogTitle>
          </DialogHeader>

          {activeDetail && (
            <div className="space-y-5 mt-1">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-red-50 rounded-xl p-3 text-center border border-red-100">
                  <p className="text-xs text-gray-500 mb-1">This Month</p>
                  <p className="text-lg font-bold text-red-600">{activeDetail.thisMileage}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Last Month</p>
                  <p className="text-lg font-bold text-gray-700">{activeDetail.lastMileage}</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
                  <p className="text-xs text-gray-500 mb-1">Drop</p>
                  <p className="text-lg font-bold text-amber-600">↓ {activeDetail.drop}</p>
                </div>
              </div>

              {/* Info */}
              <div className="space-y-2.5 text-sm">
                {[
                  { icon: Bus,    label: 'Vehicle',      val: activeDetail.vehicle      },
                  { icon: Eye,    label: 'Driver',        val: activeDetail.driver       },
                  { icon: Wrench, label: 'Last Service',  val: activeDetail.lastService  },
                ].map(({ icon: Icon, label, val }) => (
                  <div key={label} className="flex items-center justify-between border-b border-gray-50 pb-2">
                    <span className="flex items-center gap-2 text-gray-500">
                      <Icon className="h-3.5 w-3.5" /> {label}
                    </span>
                    <span className="font-medium text-gray-800">{val}</span>
                  </div>
                ))}
              </div>

              {/* Recommendation */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-amber-700 mb-1">Recommendation</p>
                <p className="text-xs text-amber-800 leading-relaxed">{activeDetail.recommendation}</p>
              </div>

              {/* Entry history */}
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">
                  Recent Entries — {activeDetail.vehicle}
                </p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50">
                      {['Date','Qty (L)','Cost (₹)','Mileage','Odometer'].map((h) => (
                        <th key={h} className="text-left px-3 py-2 font-semibold text-gray-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activeDetail.entries.map((e, i) => (
                      <tr key={i} className={`border-t border-gray-50 ${i === 0 ? 'bg-red-50' : ''}`}>
                        <td className="px-3 py-2 text-gray-700">{e.date}</td>
                        <td className="px-3 py-2 text-gray-700">{e.qty}</td>
                        <td className="px-3 py-2 text-gray-700">{fmtINR(e.cost)}</td>
                        <td className={`px-3 py-2 font-semibold ${e.mileage < 7 ? 'text-red-600' : 'text-gray-700'}`}>
                          {e.mileage}
                        </td>
                        <td className="px-3 py-2 text-gray-600">{e.odometer}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                onClick={() => setActiveDetail(null)}
                className="w-full py-2.5 text-sm font-semibold bg-gray-900 text-white rounded-xl hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FuelOverview;
